import { useState, FocusEvent, useEffect, useRef } from 'react';
import PopOverComponent, {
	PopoverFeedback,
	PopOverAlignment,
	PopOverType
} from '../pop-over/pop-over.component';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import sanitiseString from '../../lib/utils/sanitise-string';
import {
	IconsProps,
	IconTravelAirportComponent
} from '@vaa-component-lib/component.atom.icons';
import { ListField, ListFieldList } from './list-field.styles';
import DropDownListComponent from '../drop-down-list/drop-down-list.component';
import { SelectOption } from '../../types/config';
import { useFormContext } from 'react-hook-form';
import { Gateway } from '../../types/api';

export interface Option {
	label: string;
	value: string | number;
	selected?: boolean;
}

export interface ListFieldComponentProps {
	label: string | undefined;
	name: string;
	placeholder?: string | undefined;
	id: string | undefined;
	value: Gateway | undefined;
	iconChild?: React.ReactElement<IconsProps>;
	options?: Option[];
	hasError?: boolean;
	feedback?: string;
	isDisabled: boolean;
	switchFocusTo?: () => void;
}

const ListFieldComponent = ({
	label,
	name,
	placeholder,
	id,
	value,
	iconChild,
	options,
	hasError,
	feedback,
	isDisabled = false,
	switchFocusTo
}: ListFieldComponentProps) => {
	const { setValue } = useFormContext();
	const [showPopOver, setShowPopOver] = useState<boolean>(false);
	const selected = options?.find((item) => item?.selected === true);
	const [selectedValue, setSelectedValue] = useState<string>(
		`${selected?.label || ''}`
	);

	const ref = useRef<HTMLDivElement>(null);
	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);
	const isDesktop = useMediaQuery(`(min-width: ${BreakPointToken.MdMin}px)`);

	const handleSelection = (
		option: SelectOption,
		validate = true,
		switchTo = true
	) => {
		setSelectedValue(`${option.label}`);
		setValue(name, option.value, { shouldValidate: validate });
		setShowPopOver(false);

		if (switchFocusTo && !isMobile && switchTo) {
			setTimeout(() => switchFocusTo(), 50);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setShowPopOver(false);
		}
	};

	const handleFocus = () => {
		setShowPopOver(true);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		if (
			e.relatedTarget &&
			ref.current &&
			!ref.current.contains(e.relatedTarget)
		) {
			setShowPopOver(false);
		}
	};

	useEffect(() => {
		if (document) {
			if (showPopOver) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showPopOver]);

	useEffect(() => {
		if (isDisabled) {
			setValue(name, '', { shouldValidate: false });
			setSelectedValue('');
		}
	}, [isDisabled]);

	useEffect(() => {
		// Select gateway option if it's available and criteria is passed in
		if (value && options?.length > 0) {
			handleSelection(
				{
					label: value.description,
					value: value.code,
					selected: true
				} as SelectOption,
				false,
				false
			);
		}
	}, []);

	const popOverId = `popover-${sanitiseString(label || '')}`;

	return (
		<ListField ref={ref} data-cy="list-field-component">
			{isMobile && (
				<CriteriaTriggerComponent
					label={label}
					value={selectedValue}
					iconChild={iconChild}
					hasError={hasError}
					errorMessage={feedback}
					onClick={() => setShowPopOver(true)}
					aria={{ controls: popOverId, expanded: showPopOver }}
					disabled={isDisabled}
				/>
			)}
			{!isMobile && (
				<TextFieldComponent
					{...{
						label,
						placeholder,
						id,
						value
					}}
					onFocus={handleFocus}
					label={label}
					placeholder={''}
					value={selectedValue}
					onBlur={handleBlur}
					autoComplete="off"
					spellCheck={false}
					aria-controls={popOverId}
					aria-expanded={showPopOver}
					readonly
					feedback={showPopOver ? '' : feedback}
					hasError={hasError}
					role="combobox"
					disabled={isDisabled}
					aria-label={`${label} - begin entering your search to receive results`}
					auto-complete="list"
					aria-haspopup="true"
				/>
			)}

			<PopOverComponent
				id={popOverId}
				isVisible={showPopOver}
				title={label}
				type={PopOverType.Selector}
				minWidth={375}
				onClose={() => setShowPopOver(false)}
				alignment={isDesktop ? PopOverAlignment.Left : PopOverAlignment.Right}
				isOverflow={true}>
				{options?.length > 0 ? (
					<ListFieldList>
						<DropDownListComponent
							items={options as SelectOption[]}
							onSelect={(option: SelectOption) => handleSelection(option)}
							isOpen={showPopOver}
							itemIcon={<IconTravelAirportComponent />}
							clearSelection={isDisabled}
						/>
					</ListFieldList>
				) : (
					<PopoverFeedback message={feedback} />
				)}
			</PopOverComponent>
		</ListField>
	);
};

export default ListFieldComponent;
